import { useObserver, useSubtree } from "subtree.js/react";
import type { WorkspaceOption } from "../../core/primitives/workspace";
import { ChooseWorkspaceActions, ChooseWorkspaceState } from "./choose-workspace-model";

function StoreBadge({ option }: { option: WorkspaceOption }) {
  const name = option.store.name ?? "Your shop";
  if (option.store.logoUrl) {
    return (
      <img
        src={option.store.logoUrl}
        alt=""
        className="h-9 w-9 shrink-0 rounded-thumb border border-line object-cover"
      />
    );
  }
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-thumb bg-accent text-sm font-semibold text-ink">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

function OptionRow({ option, onChoose }: { option: WorkspaceOption; onChoose: () => void }) {
  const { store, location } = option;
  const place = location ? [location.city, location.province].filter(Boolean).join(", ") : "";
  return (
    <button
      type="button"
      onClick={onChoose}
      className="flex w-full items-center gap-3 rounded-btn border border-line bg-surface px-3 py-3 text-left transition-colors hover:border-accent"
    >
      <StoreBadge option={option} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-white">
          {store.name ?? "Your shop"}
          <span className="font-normal text-muted"> · {location ? location.name : "no location yet"}</span>
        </span>
        {place && <span className="block truncate text-[13px] text-muted">{place}</span>}
      </span>
    </button>
  );
}

/** Shown on every fresh load of an account with more than one shop × location, before any shop page. */
export function ChooseWorkspacePage() {
  const state = useSubtree(ChooseWorkspaceState);
  const actions = useSubtree(ChooseWorkspaceActions);
  const observer = useObserver();
  const status = observer.watch(state.status);
  const options = observer.watch(state.options);

  return (
    <div className="flex min-h-screen items-center justify-center bg-app px-4 py-10">
      <div className="panel w-full max-w-md p-6">
        <h1 className="text-lg font-semibold text-white">Where are you working?</h1>
        <p className="mt-1 text-[13px] leading-relaxed text-muted">
          Pick a shop and location. Carts, scans, live shows and shipping follow it; your inventory is shared across
          locations.
        </p>
        <div className="mt-5 space-y-2">
          {status.kind === "error" ? (
            <div className="text-center">
              <p className="text-[13px] text-muted">{status.message}</p>
              <button type="button" onClick={actions.retry} className="btn-primary btn-sm mt-3">
                Try again
              </button>
            </div>
          ) : status.kind !== "loaded" ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            </div>
          ) : (
            options.map((option) => (
              <OptionRow
                key={`${option.store.id}:${option.location?.id ?? ""}`}
                option={option}
                onChoose={() => actions.choose(option)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
